// Top bar from the UX concepts: logo, Plan · Library · Maps, search, Subscribe, help and account.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { ProfileDialog } from '@/auth/ProfileDialog';
import { libraryHome } from '@/features/library/session';
import { Avatar } from '@/people/Avatar';
import { useAccount } from '@/auth/useAccount';
import styles from './AppShell.module.css';

export function AppShell() {
  const { t } = useTranslation();
  // Re-render on every navigation so the Library tab picks up the folder the Library last had open.
  useLocation();
  const tabs = [
    { to: '/plan', label: t('nav.plan') },
    // Back to the folder the user was in.
    { to: libraryHome(), label: t('nav.library') },
    { to: '/maps', label: t('nav.maps') },
  ];

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <span className={styles.logo}>
          Xpedition<span className={styles.logoAccent}>2</span>
        </span>
        <nav className={styles.tabs} aria-label="Main">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) => (isActive ? `${styles.tab} ${styles.active}` : styles.tab)}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
        <input
          className={styles.search}
          type="search"
          placeholder={t('search.placeholder')}
          aria-label={t('search.placeholder')}
        />
        <div className={styles.actions}>
          <button type="button" className={styles.subscribe}>
            {t('nav.subscribe')}
          </button>
          <AccountMenu />
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

/** The avatar: opens a menu with the user's name, their profile and sign out. */
function AccountMenu() {
  const { t } = useTranslation();
  const account = useAccount();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Close on a click elsewhere or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.account} ref={container}>
      <button
        type="button"
        className={styles.avatar}
        title={account.name}
        aria-label={t('nav.account')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar personGuid={account.personGuid} initials={account.initials} />
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          <div className={styles.menuHeader}>
            <div className={styles.menuName}>{account.name}</div>
            {account.email && <div className={styles.menuEmail}>{account.email}</div>}
          </div>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              setProfileOpen(true);
            }}
          >
            {t('nav.profile')}
          </button>
          <button type="button" role="menuitem" className={styles.menuItem} onClick={account.signOut}>
            {t('nav.signOut')}
          </button>
        </div>
      )}
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
